import { NotificationTopicOwnersSettingsSection } from "@/components/NotificationTopicOwnersSettingsSection";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

const NOTIFICATION_STAFF_USERS_QUERY_KEY = ["notifications", "staff-user-options"] as const;

function fetchNotificationStaffUsers(): Promise<{
  users: Array<{ userId: string; email: string; displayName: string | null }>;
}> {
  return apiFetch("/api/notifications/staff-user-options");
}

export function AccountingSettingsPage(): JSX.Element {
  const canEdit = useHubModuleAdmin("accounting");

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Accounting module settings: vendor invoice, early payment, CBP monthly statement, and receipt discrepancy distribution lists.">
          Accounting · Settings
        </PageTitleWithHelp>
      </header>

      <NotificationTopicOwnersSettingsSection
        topic="ap_vendor_invoice"
        title="Vendor invoice distribution"
        hoverHint="Hub users in this group receive email and an in-app notification when a vendor invoice is ready for tariff, shipping, and handling. That happens after the matched purchase invoice (GIPI) exists, including when the vendor invoice was entered before the receipt."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Accounting module administrators can edit this distribution list."
      />

      <NotificationTopicOwnersSettingsSection
        topic="ap_cbp_monthly_statement"
        title="CBP monthly statement"
        hoverHint="Hub users in this group receive email and an in-app notification when a CBP vendor invoice is saved. That is the Periodic Monthly Statement amount. It is sent then so funds can be in the account before Customs debits, without waiting for the Megaventory TSH."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Accounting module administrators can edit this distribution list."
      />

      <NotificationTopicOwnersSettingsSection
        topic="ap_early_payment"
        title="Early payment distribution"
        hoverHint="Hub users in this group receive email and an in-app notification when someone requests early payment on a vendor invoice, including before a Megaventory GIPI exists."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Accounting module administrators can edit this distribution list."
      />

      <NotificationTopicOwnersSettingsSection
        topic="ap_receipt_discrepancy"
        title="Receipt discrepancy distribution"
        hoverHint="Hub users in this group receive in-app notifications when receiving or invoice pairing flags an item for Accounting office review."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Accounting module administrators can edit this distribution list."
      />
    </div>
  );
}
